import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { constants } from "@qgiv/core-js";
import { CardGroup, radioKeyboardEventHandler } from "@qgiv/core-donor";
import { getShouldSetRemainingFormToShow } from "@qgiv/p2p-registration";
import { setAppShowRegistrationRole } from "../../../redux/slices/appSettingsSlice";
import ConnectedCategoryCard from "./ConnectedCategoryCard";
import ConnectedCategoriesHeading from "./ConnectedCategoriesHeading";

const detectCategorySelected = (Category) =>
    !!(Category && Category[0] && Category[0].length > 0);

const Categories = ({
    allowSubRegistrantToChangeRegRole,
    categories = [],
    controlId,
    currentRegistration,
    currentRegistrationCategory = [],
    isPrimaryRegistrant,
    isCms,
    isRegRoleIndividualOnly,
    primaryRegistration,
    setAppShowRemainingForm,
    setCurrentRegistrationCategory,
    setFieldTouched,
    setFieldValue,
    showRemainingForm,
    updateCategoryOnCurrentRegistration,
    updateCategoryOnRegistrations,
    values = {},
}) => {
    const {
        ENUMS: {
            EntityType: { REGISTRATION },
        },
    } = constants;
    const dispatch = useDispatch();

    const { Category = [] } = values;
    const [categorySelected, setCategorySelected] = useState(
        detectCategorySelected(Category),
    );

    useEffect(() => {
        // If Category and Full Registration Roles are present in new initial value then
        // we should show the remaining form, otherwise hide it to await selections.
        const shouldSetRemainingFormToShow =
            getShouldSetRemainingFormToShow(values);

        dispatch(setAppShowRemainingForm(shouldSetRemainingFormToShow));

        // Category may be unset by a Classification change. When this happens we want to make
        // sure Category section is highlighted by hiding the rest of the form
        if (!categorySelected && !isCms && showRemainingForm) {
            dispatch(setAppShowRemainingForm(false));
        }
        // Needed for having a preselected Category if it is the only Category and the only role is Individual
        if (categorySelected && isRegRoleIndividualOnly && !showRemainingForm) {
            dispatch(setAppShowRemainingForm(true));
            setFieldValue("Registration_Role", [`${REGISTRATION}`]);
        }

        if (categorySelected) {
            // Preselect Team and show form when Additional Participant and
            // allowSubRegistrantToChangeRegRole is disabled
            if (!isPrimaryRegistrant && !allowSubRegistrantToChangeRegRole) {
                dispatch(setAppShowRemainingForm(true));
                setFieldTouched("Registration_Role");
                setFieldValue(
                    "Registration_Role",
                    primaryRegistration.Registration_Role,
                );
                setFieldValue("Team", primaryRegistration.Team);
                setFieldValue("Team_Captain", false);
            }
            dispatch(setAppShowRegistrationRole(true));
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categorySelected]);

    const displayCategories = categories.length > 0;
    // Since we are pre-selecting the category if only one category is available
    // we will need to know this for displaying edit icon
    const multipleCategories = categories.length > 1;

    useEffect(() => {
        setCategorySelected(detectCategorySelected(Category));
        dispatch(setCurrentRegistrationCategory(Category));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [Category]);

    useEffect(() => {
        if (updateCategoryOnCurrentRegistration) {
            dispatch(
                updateCategoryOnRegistrations({
                    index: currentRegistration,
                    Category: currentRegistrationCategory,
                }),
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateCategoryOnCurrentRegistration]);

    const categoryRefs = categories.map(() => React.createRef());
    const handleKeyDown = (event, index) => {
        if (categorySelected) return;
        radioKeyboardEventHandler(event, index, categoryRefs);
    };

    return (
        displayCategories && (
            <>
                <ConnectedCategoriesHeading controlId={controlId} />
                <CardGroup legend="Choose a category" colWidth={12}>
                    {categories.map((category, i) => {
                        const { id } = category;

                        const isSelectedCategory =
                            Category.length === 1 &&
                            `${Category[0]}` === `${id}`;

                        const displayCategoryCard =
                            Category.length === 0 || isSelectedCategory;

                        return (
                            displayCategoryCard && (
                                // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                                <div
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={i}
                                    onKeyDown={(e) => handleKeyDown(e, i)}
                                >
                                    <ConnectedCategoryCard
                                        id={id}
                                        name="Category"
                                        type="checkbox"
                                        multipleCategories={multipleCategories}
                                        categoryRef={categoryRefs[i]}
                                        controlId={controlId}
                                    />
                                </div>
                            )
                        );
                    })}
                </CardGroup>
            </>
        )
    );
};

Categories.propTypes = {
    controlId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    currentRegistration: PropTypes.number.isRequired,
    currentRegistrationCategory: PropTypes.arrayOf(PropTypes.string),
    categories: PropTypes.arrayOf(PropTypes.shape({})),
    values: PropTypes.shape({
        Category: PropTypes.arrayOf(PropTypes.string),
    }),
    setCurrentRegistrationCategory: PropTypes.func.isRequired,
    updateCategoryOnCurrentRegistration: PropTypes.bool.isRequired,
    updateCategoryOnRegistrations: PropTypes.func.isRequired,
    showRemainingForm: PropTypes.bool.isRequired,
    setFieldValue: PropTypes.func.isRequired,
    setFieldTouched: PropTypes.func.isRequired,
    setAppShowRemainingForm: PropTypes.func.isRequired,
    isPrimaryRegistrant: PropTypes.bool.isRequired,
    isCms: PropTypes.bool.isRequired,
    isRegRoleIndividualOnly: PropTypes.bool.isRequired,
    primaryRegistration: PropTypes.shape({
        Registration_Role: PropTypes.arrayOf(PropTypes.string),
        Team: PropTypes.string,
    }).isRequired,
    allowSubRegistrantToChangeRegRole: PropTypes.bool.isRequired,
};

export default Categories;
